import {
  EMPTY_ENRICHMENT_AGGREGATES,
  type OptionFlowTickerAggregate,
} from 'utils/shared/trades/aggregateOptionFlow';
import { getSectorShortName } from 'utils/shared/trades/sectors';

export type OptionFlowAggregateCsvValue = string | number;
export type OptionFlowAggregateCsvRow = Record<string, OptionFlowAggregateCsvValue>;

interface OptionFlowAggregateCsvOptions {
  isWeekly?: boolean;
}

const round = (value: number, precision = 2): number => {
  const multiplier = 10 ** precision;
  return Math.round((value + Number.EPSILON) * multiplier) / multiplier;
};

const numberOrBlank = (value: number | null | undefined, precision = 2): number | '' =>
  value == null || !Number.isFinite(value) ? '' : round(value, precision);

const getPercentShare = (part: number, total: number): number | '' =>
  total > 0 ? round((part / total) * 100, 2) : '';

/**
 * An average OTM over an empty class is 0/0. Reporting it as `0` reads as "these
 * trades were at the money" when the truth is that there were no such trades, so
 * the cell stays blank until the class has contracts behind it.
 */
const getClassOtm = (otm: number, contracts: number): number | '' =>
  contracts > 0 ? numberOrBlank(otm / 100, 4) : '';

const getPremiumPutFlowPercent = (aggregate: OptionFlowTickerAggregate): number | '' => {
  const directionalPremium = aggregate.putPremium + aggregate.callPremium;

  // The product shows a neutral 50/50 gauge for a ticker with no directional prints,
  // which is right on screen and wrong in a data file — a reader sorting this column
  // would see the emptiest tickers as the most perfectly balanced. `Directional
  // Contracts` and `Directional Coverage %` already report the zero.
  return directionalPremium > 0 ? getPercentShare(aggregate.putPremium, directionalPremium) : '';
};

/**
 * Stable ticker-level option-flow export. Unlike the visible table, this schema
 * never changes with Total/Net display mode and includes both gross and signed
 * analytics in the same row.
 */
export const formatOptionFlowAggregateCsvData = (
  aggregates: OptionFlowTickerAggregate[],
  { isWeekly = false }: OptionFlowAggregateCsvOptions = {},
): OptionFlowAggregateCsvRow[] =>
  aggregates.map((aggregate) => {
    const putFlowPercent = getPremiumPutFlowPercent(aggregate);
    // Aggregates that crossed a JSON boundary (injected/screenshot pages) lose the getter.
    const enrichment = aggregate.getEnrichmentAggregates?.() ?? EMPTY_ENRICHMENT_AGGREGATES;
    const directionalContracts = aggregate.putVolume + aggregate.callVolume;

    return {
      Ticker: aggregate.ticker,
      Company: aggregate.tickerName,
      Sector: getSectorShortName(aggregate.sector || ''),
      'Market Cap ($)': aggregate.marketCap > 0 ? numberOrBlank(aggregate.marketCap, 2) : '',
      'Trade Count': numberOrBlank(aggregate.tradeCount, 0),
      'Unique Contracts': numberOrBlank(enrichment.uniqueContracts, 0),
      'Flow Sentiment %': numberOrBlank(aggregate.sentimentScore, 2),
      'Put Flow %': putFlowPercent,
      'Call Flow %': typeof putFlowPercent === 'number' ? round(100 - putFlowPercent, 2) : '',
      'Put Contract %': getPercentShare(aggregate.putVolume, directionalContracts),
      'Call Contract %': getPercentShare(aggregate.callVolume, directionalContracts),
      'Put Premium ($)': numberOrBlank(aggregate.putPremium, 2),
      'Call Premium ($)': numberOrBlank(aggregate.callPremium, 2),
      'Put Contracts': numberOrBlank(aggregate.putVolume, 2),
      'Call Contracts': numberOrBlank(aggregate.callVolume, 2),
      'Total Contracts': numberOrBlank(aggregate.contracts, 2),
      'Total Premium ($)': numberOrBlank(aggregate.premium, 2),
      'Directional Contracts': numberOrBlank(aggregate.directionalContracts, 2),
      'Non-Directional Contracts': numberOrBlank(aggregate.neutralContracts, 2),
      'Directional Coverage %': numberOrBlank(aggregate.directionalCoveragePercent, 2),
      'Bullish Premium ($)': numberOrBlank(aggregate.bullishPremium, 2),
      'Bearish Premium ($)': numberOrBlank(aggregate.bearishPremium, 2),
      'Non-Directional Premium ($)': numberOrBlank(aggregate.neutralPremium, 2),
      'Net Trade Count': numberOrBlank(aggregate.netTradeCount, 0),
      'Net Contracts': numberOrBlank(aggregate.netContracts, 2),
      'Net Premium ($)': numberOrBlank(aggregate.netPremium, 2),
      'Net Premium %': numberOrBlank(aggregate.netPremiumShare, 2),
      OTM: getClassOtm(aggregate.otm, aggregate.contracts),
      'Momentum Multiple': numberOrBlank(isWeekly ? aggregate.volumeGrowth : aggregate.momentum, 2),
      'Avg Price ($)': numberOrBlank(aggregate.price, 2),
      'Avg Expiry Days': numberOrBlank(aggregate.expiry, 0),
      'Avg Implied Vol': numberOrBlank(enrichment.avgImpliedVol, 8),
      'Avg NBBO Mid IV': numberOrBlank(enrichment.avgNbboMidIv, 8),
      'Avg Probability of Touch': numberOrBlank(enrichment.avgProbabilityOfTouch, 8),
      'Avg Risk-Free Rate': numberOrBlank(enrichment.avgRiskFreeRate, 8),
      'Gross Delta-Adjusted Notional ($)': numberOrBlank(enrichment.grossDeltaAdjustedNotional, 2),
      'Net Delta-Adjusted Notional ($)': numberOrBlank(enrichment.netDeltaAdjustedNotional, 2),
      'Net Delta Shares': numberOrBlank(enrichment.netDeltaShares, 4),
      'Net Gamma Exposure ($ / 1% Move)': numberOrBlank(enrichment.netGammaExposure, 4),
      'Net Theta Exposure ($ / Day)': numberOrBlank(enrichment.netThetaExposure, 4),
      'Net Vega Exposure ($ / Vol Point)': numberOrBlank(enrichment.netVegaExposure, 4),
      'Net Vanna Exposure': numberOrBlank(enrichment.netVannaExposure, 8),
      'Net Charm Exposure': numberOrBlank(enrichment.netCharmExposure, 8),
      'Unusual OTM': getClassOtm(aggregate.unusualOtm, aggregate.unusualContracts),
      'Unusual Contracts': numberOrBlank(aggregate.unusualContracts, 2),
      'Unusual Premium ($)': numberOrBlank(aggregate.unusualPremium, 2),
      'Net Unusual Contracts': numberOrBlank(aggregate.netUnusualContracts, 2),
      'Net Unusual Premium ($)': numberOrBlank(aggregate.netUnusualPremium, 2),
      'Sweep OTM': getClassOtm(aggregate.sweepOtm, aggregate.sweepContracts),
      'Sweep Contracts': numberOrBlank(aggregate.sweepContracts, 2),
      'Sweep Premium ($)': numberOrBlank(aggregate.sweepPremium, 2),
      'Net Sweep Contracts': numberOrBlank(aggregate.netSweepContracts, 2),
      'Net Sweep Premium ($)': numberOrBlank(aggregate.netSweepPremium, 2),
      'Split OTM': getClassOtm(aggregate.splitOtm, aggregate.splitContracts),
      'Split Contracts': numberOrBlank(aggregate.splitContracts, 2),
      'Split Premium ($)': numberOrBlank(aggregate.splitPremium, 2),
      'Net Split Contracts': numberOrBlank(aggregate.netSplitContracts, 2),
      'Net Split Premium ($)': numberOrBlank(aggregate.netSplitPremium, 2),
      'Block OTM': getClassOtm(aggregate.blockOtm, aggregate.blockContracts),
      'Block Contracts': numberOrBlank(aggregate.blockContracts, 2),
      'Block Premium ($)': numberOrBlank(aggregate.blockPremium, 2),
      'Net Block Contracts': numberOrBlank(aggregate.netBlockContracts, 2),
      'Net Block Premium ($)': numberOrBlank(aggregate.netBlockPremium, 2),
      'Golden Sweep OTM': getClassOtm(aggregate.goldenSweepOtm, aggregate.goldenSweepContracts),
      'Golden Sweep Contracts': numberOrBlank(aggregate.goldenSweepContracts, 2),
      'Golden Sweep Premium ($)': numberOrBlank(aggregate.goldenSweepPremium, 2),
      'Net Golden Sweep Contracts': numberOrBlank(aggregate.netGoldenSweepContracts, 2),
      'Net Golden Sweep Premium ($)': numberOrBlank(aggregate.netGoldenSweepPremium, 2),
    };
  });
